/**
 * Whose name is on the screen.
 *
 * An installation can carry its own product name, colour and logo (set on
 * the server with `bv brand`). They are fetched once at start -- before
 * anyone signs in, because the login page shows them -- applied to the
 * page title and to the `app.name` text every screen already uses, and
 * remembered on the device so the van app shows the right name offline.
 */
import { useSyncExternalStore } from 'react'
import i18n from './i18n'

export interface Branding {
  name: string
  short_name: string
  color: string | null
  support_email: string | null
  support_phone: string | null
  logo_url: string | null
  custom: boolean
}

const KEY = 'bv.branding'
let current: Branding | null = null
const listeners = new Set<() => void>()

export function getBranding(): Branding | null { return current }

export function onBranding(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export function useBranding(): Branding | null {
  return useSyncExternalStore(onBranding, getBranding, getBranding)
}

function apply(b: Branding) {
  const renamed = b.custom && current?.name !== b.name
  current = b
  if (b.custom) {
    // The same name in both languages: a brand is not translated.
    for (const lng of ['en', 'ar']) i18n.addResource(lng, 'translation', 'app.name', b.name)
    // Screens already drawn re-read their text.
    if (renamed && i18n.isInitialized) void i18n.changeLanguage(i18n.language)
  }
  document.title = b.name
  document.querySelector('meta[name="apple-mobile-web-app-title"]')
    ?.setAttribute('content', b.short_name)
  listeners.forEach((fn) => fn())
}

export async function loadBranding(): Promise<void> {
  try {
    const cached = localStorage.getItem(KEY)
    if (cached) apply(JSON.parse(cached) as Branding)
  } catch { /* private mode: no cache, fine */ }
  try {
    const res = await fetch('/api/v1/branding')
    if (!res.ok) return
    const b = (await res.json()) as Branding
    apply(b)
    try { localStorage.setItem(KEY, JSON.stringify(b)) } catch { /* fine */ }
  } catch { /* offline, or an older server without branding: keep the default */ }
}
